import { useEffect, useState, useCallback, useMemo, memo, useRef } from 'react';
import { Controller, FormProvider, useForm, useWatch } from 'react-hook-form';
import { debounce } from 'lodash';
import RightSideModal from "../Modal/right-side-modal";
import Card from "../card";
import InputField from '../form-fields/InputField';
import EmailInput from '../form-fields/EmailInput';
import Radio from "../form-fields/radio";
import FancySingleSelectDropdown from '../form-fields/FancyDropDowns/SingleFancyDropdown';
import MultiFancyDropdown from '../form-fields/FancyDropDowns/MultiFancyDropdown';
import apiHelper from "../../utils/apiHelper";
import { ContractorService, ContractorVM, LookupService, LookupTypesEnum } from "@propertelligent/client-api";
import { GetImages } from '../../utils/GetImages';
import PhoneInput from '../form-fields/PhoneInput';
import { formatLabel } from '../../utils/helper';
import { useFieldChangesSnapshot } from '../../hooks/useFieldChangesSnapshot';

const MemoizedFancySingleSelectDropdown = memo(FancySingleSelectDropdown);
const MemoizedMultiFancyDropdown = memo(MultiFancyDropdown);
const MemoizedInputField = memo(InputField);
const MemoizedEmailInput = memo(EmailInput);
const MemoizedRadio = memo(Radio);

interface Option {
  Id: string | number;
  Name: string;
}

interface ContractorValues {
  serviceType?: string | number;
  serviceSubType?: (string | number)[];
  email: string;
  firstName: string;
  isInHouse: string;
  lastName: string;
  phoneNumber: string;
}

interface EditContractorFormProps {
  isModalOpen: boolean;
  setModalOpen: (open: boolean) => void;
  details?: ContractorVM | null;
  fetchData?: () => void;
}

const debouncedFetchServiceSubTypes = debounce(async (
  value: number,
  callback: (data: Option[]) => void,
  errorCallback: (error: any) => void
) => {
  try {
    const response = await apiHelper(
      LookupService.getApiLookup1,
      { showNotification: false },
      LookupTypesEnum.ServiceSubTypes,
      value
    );
    callback(
      response?.data?.success?.map((item: any) => ({
        Id: item.id || item.Id || 0,
        Name: formatLabel(item.name || item.description || '')
      })) || []
    );
  } catch (err) {
    errorCallback(err);
  }
}, 300);

const EditContractorForm: React.FC<EditContractorFormProps> = ({
  isModalOpen,
  setModalOpen,
  details,
  fetchData
}) => {
  const [serviceTypes, setServiceTypes] = useState<Option[]>([]);
  const [serviceSubTypes, setServiceSubTypes] = useState<Option[]>([]);
  const [isSubTypeHidden, setIsSubTypeHidden] = useState(false);
  const [isSubTypeDisabled, setIsSubTypeDisabled] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const methods = useForm<ContractorValues>({
    mode: "onChange",
    defaultValues: useMemo(() => ({
      serviceType: '',
      serviceSubType: [],
      email: '',
      firstName: '',
      isInHouse: '0',
      lastName: '',
      phoneNumber: ''
    }), [])
  });

  const watchedServiceType = methods.watch("serviceType");

  // methods.formState.isDirty never resets to false once any field has been touched,
  // even after editing it back to its original value (react-hook-form's known
  // limitation, see ui-consistency.md SS10 gotcha 9) -- this form's Contact Number/
  // Subtype fields hit exactly that symptom. hasFieldChanges is a second, independent
  // signal for the primitive fields; serviceSubType is array-valued (outside this
  // hook's Primitive constraint), so it's ORed in via its own dirtyFields entry instead.
  // That entry resets correctly for the common "select/deselect back to the original
  // set, same order" case (gotcha 10), but RHF's underlying deepEqual is order-sensitive
  // -- toggling an item off and back on can reorder the array and leave dirtyFields
  // .serviceSubType stuck true even though the resulting set matches. Accepted as a
  // narrow, pre-existing RHF limitation rather than re-implemented here.
  // phoneNumber is tracked separately (below), not through this hook's generic
  // String()-comparison -- see the comment there for why.
  const { hasFieldChanges, setSnapshot } = useFieldChangesSnapshot<
    Omit<ContractorValues, "serviceSubType" | "phoneNumber">
  >(methods.control, [
    "serviceType",
    "email",
    "firstName",
    "isInHouse",
    "lastName",
  ]);

  // react-phone-input-2 always reports its onChange value as digits that include the
  // country calling code (e.g. "16026459161"), but a contractor's stored phoneNumber
  // from the API is not guaranteed to include it (e.g. "6026459161") -- so the very
  // first interaction with the field re-emits the *same displayed number* as a
  // different raw string, permanently tripping a plain string comparison even after
  // the user reverts to the original digits. Normalize both sides by stripping
  // non-digits and dropping a leading US/Canada "1" calling code before comparing.
  const normalizePhoneForCompare = useCallback((value?: string | null) => {
    const digits = (value ?? "").replace(/\D/g, "");
    return digits.length === 11 && digits.startsWith("1") ? digits.slice(1) : digits;
  }, []);
  const originalPhoneRef = useRef<string>("");
  // useWatch (not methods.watch) -- watch() called directly in render can lag a render
  // behind on rapid successive updates, which made this comparison flicker; useWatch's
  // subscription-based model keeps it synchronous with the actual form state.
  const watchedPhoneNumber = useWatch({ control: methods.control, name: "phoneNumber" });
  const phoneNumberChanged =
    normalizePhoneForCompare(watchedPhoneNumber) !== normalizePhoneForCompare(originalPhoneRef.current);

  const hasUnsavedChanges =
    (methods.formState.isDirty && (hasFieldChanges || phoneNumberChanged)) ||
    Boolean(methods.formState.dirtyFields.serviceSubType);

  const fetchLookupData = useCallback(async (service: any, lookupType: LookupTypesEnum) => {
    const response = await apiHelper(service, { showNotification: false }, lookupType);
    return response?.data?.success?.map((val: any) => ({
      Id: val.id || val.Id || 0,
      Name: val.description || val.Name || val.name || ''
    })) || [];
  }, []);

  const fetchInitialData = useCallback(async () => {
    try {
      setIsLoading(true);
      const serviceTypesData = await fetchLookupData(LookupService.getApiLookup, LookupTypesEnum.ServiceTypes);
      setServiceTypes(serviceTypesData);
    } catch (error) {
      console.error("Error loading initial data:", error);
    } finally {
      setIsLoading(false);
    }
  }, [fetchLookupData]);

  const prevServiceTypeRef = useRef<string | number | null>(null);

  const handleServiceTypeChange = useCallback(
    (value: string | number) => {
      const numericValue = Number(value);

      if (prevServiceTypeRef.current !== numericValue) {
        // shouldDirty: true -- without it, dirtyFields.serviceSubType never flips even
        // though the selection was actually cleared, so switching Service Type A->B->A
        // (subtypes cleared, then the user picks A again) left hasUnsavedChanges reading
        // false: hasFieldChanges matched (serviceType back to its snapshot) and
        // dirtyFields.serviceSubType was never set, silently skipping the Cancel/
        // unsaved-changes guard for a real change to what would be submitted.
        methods.setValue("serviceSubType", [], { shouldDirty: true });
        setServiceSubTypes([]);
      }

      prevServiceTypeRef.current = numericValue;

      const selectedOption = serviceTypes.find(
        (opt) => Number(opt.Id) === numericValue
      );

      const typeName = selectedOption?.Name?.toLowerCase() || "";
      const isPlumbingOrElectrical =
      typeName.includes("plumbing") || typeName.includes("electrical");

      const shouldHide = numericValue === 999 || isPlumbingOrElectrical;
      const shouldDisable = numericValue === 999 || isPlumbingOrElectrical;

      setIsSubTypeHidden(shouldHide);
      setIsSubTypeDisabled(shouldDisable);

      if (!shouldHide && numericValue) {
        debouncedFetchServiceSubTypes(
          numericValue,
          (data) => {
            setServiceSubTypes(data);
            setIsSubTypeDisabled(false);

            const validIds = new Set(data.map((opt) => Number(opt.Id)));
            const currentValue = (methods.getValues("serviceSubType") || []) as (string | number)[];
            const reconciled = currentValue.filter((id) => validIds.has(Number(id)));
            if (reconciled.length !== currentValue.length) {
              methods.setValue("serviceSubType", reconciled, { shouldValidate: true, shouldDirty: true });
            }
          },
          console.error
        );
      }
    },
    [methods, serviceTypes]
  );

  useEffect(() => {
    if (!watchedServiceType) return;
    if (!serviceTypes.length) return;

    handleServiceTypeChange(watchedServiceType);
  }, [watchedServiceType, serviceTypes, handleServiceTypeChange]);

  useEffect(() => {
    if (isModalOpen && details) {
      fetchInitialData();
      const defaultServiceType = details.serviceTypesIds?.[0] ?? '';
      const defaultSubTypes = details.serviceSubTypesIds ?? [];

       prevServiceTypeRef.current = defaultServiceType;

      const baseValues = {
        firstName: details.firstName ?? '',
        lastName: details.lastName ?? '',
        email: details.email ?? '',
        phoneNumber: details.phoneNumber ?? '',
        isInHouse: details.isInHouse ? '1' : '0',
        serviceType: defaultServiceType,
        serviceSubType: defaultSubTypes
      };
      methods.reset(baseValues);
      setSnapshot(baseValues);
      originalPhoneRef.current = baseValues.phoneNumber;
    }

    return () => {
      debouncedFetchServiceSubTypes.cancel();
    };
  }, [isModalOpen, details, fetchInitialData, methods]);

  const onSubmit = useCallback(async (data: ContractorValues) => {
    if (!details) return;

    try {
      setIsLoading(true);
      const requestData = {
        id: details.id,
        firstName: data.firstName,
        lastName: data.lastName,
        email: data.email,
        phoneNumber: data.phoneNumber,
        isInHouse: data.isInHouse === '1',
        ServiceTypesIds: data.serviceType ? [Number(data.serviceType)] : [],
        serviceSubTypesIds: data.serviceSubType?.map(Number) || [],
      };

       const response = await apiHelper(
        ContractorService.putApiContractor,
        {
          title: "Contractor Updated",
          successMessage: `${data.firstName} ${data.lastName} updated successfully`,
          showNotification: true,
          successIcon: <img src={GetImages.toastContractorIcon} alt="success" width="20" height="20" />
        },
        requestData
      );

       // After a successful API call, refresh the data and close the modal
      if (response?.data?.success) {
        if (fetchData) {
          fetchData(); // This triggers the refetch on the details page
        }
        setModalOpen(false);
      }

    } catch (error) {
      console.error("Update failed:", error);
    } finally {
      setIsLoading(false);
    }
  }, [details, setModalOpen]);

  const thingToKnow = useMemo(() => [
    {
      points: [
        { text: "The entire property is designed for one household, meaning there are no multiple units within the structure." },
        { text: "The owner has complete control over the property." }
      ]
    }
  ], []);

  return (
    <RightSideModal
      isOpen={isModalOpen}
      onClose={() => setModalOpen(false)}
      title="Edit Contractor"
      buttonlabel="Save"
      formId="EditContractorForm"
      maxWidth={550}
      isDirty={hasUnsavedChanges}
      isLoading={methods.formState.isSubmitting}
      isSubmitDisabled={methods.formState.isSubmitting || !hasUnsavedChanges || !methods.formState.isValid}
    >
      <Card className="no-bg">
        <FormProvider {...methods}>
          <form id="EditContractorForm" onSubmit={methods.handleSubmit(onSubmit)}>
            <div className="row">
              <div className="col-md-12">
                <label className="fw-600 mb-2">Contractor Type <span className="text-danger">*</span></label>
                <div className="d-flex align-items-center gap-4">
                  <Controller
                    name="isInHouse"
                    control={methods.control}
                    render={({ field }) => (
                      <>
                        <MemoizedRadio name={field.name} value="1" label="In House" />
                        <MemoizedRadio name={field.name} value="0" label="Third Party" />
                      </>
                    )}
                  />
                </div>
              </div>

              <div className="col-md-6">
                <MemoizedInputField
                  name="firstName"
                  label="First Name"
                  required
                  requiredMessage="First Name is required"
                  type="text"
                  placeholder="Enter First Name"
                  onlyAlphabets={true}
                  validation={{
                    minLength: {
                      value: 2, message: "First name must be at least 2 characters long"
                    },
                    maxLength: {
                      value: 20, message: "First name cannot exceed 20 characters"
                    }
                  }}
                />
              </div>

              <div className="col-md-6">
                <MemoizedInputField
                  name="lastName"
                  label="Last Name"
                  required
                  requiredMessage="Last Name is required"
                  type="text"
                  placeholder="Enter Last Name"
                  onlyAlphabets={true}
                  validation={{
                    minLength: {
                      value: 2, message: "Last name must be at least 2 characters long"
                    },
                    maxLength: {
                      value: 20, message: "Last name cannot exceed 20 characters"
                    }
                  }}
                />
              </div>

              <div className="col-md-6">
                <MemoizedEmailInput
                  name="email"
                  label="Email Address"
                  required
                  requiredMessage="Email is required"
                  placeholder="Enter Email Address"
                  validation={{ maxLength: { value: 50, message: "Email cannot exceed 50 characters" } }}
                />
              </div>

              <div className="col-md-6">
                <PhoneInput
                  name="phoneNumber"
                  label="Contact No"
                  required
                />
               
              </div>

              <div className="col-md-12">
                <MemoizedFancySingleSelectDropdown
                  name="serviceType"
                  label="Service Type"
                  required
                  requiredMessage="Contractor Type is required"
                  options={serviceTypes}
                  defaultValue={details?.serviceTypesIds?.[0] ?? ''}
                  // onChange={handleServiceTypeChange}
                  disabled={isLoading}
                />
              </div>

              {!isSubTypeHidden && (
                <div className="col-md-12">
                  <MemoizedMultiFancyDropdown
                    name="serviceSubType"
                    label="Sub Type"
                    required={!isSubTypeDisabled}
                    requiredMessage="Contractor Sub Type is required"
                    options={serviceSubTypes}
                    defaultValue={details?.serviceSubTypesIds ?? []}
                    disabled={isSubTypeDisabled || isLoading}
                  />
                </div>
              )}
            </div>
            {/* <div className="col-md-12">
              <div className="things-to-know-demo">
                <MemoizedThingsToKnow propertyTypes={thingToKnow} />
              </div>
            </div> */}
          </form>
        </FormProvider>
      </Card>
    </RightSideModal>
  );
};

export default memo(EditContractorForm);